import CustomTooltip from '../../../../shared/CustomTooltip';
import {
    AttachFile,
    Delete,
    DriveEta,
    Edit,
    ExpandLess,
    ExpandMore,
    OpenInNew,
    Notes,
    EventBusy,
} from '@mui/icons-material';
import {
    Box,
    Collapse,
    IconButton,
    Typography,
    alpha,
    lighten,
} from '@mui/material';
import { format, parse } from 'date-fns';
import React, { useState } from 'react';
import {
    useCancelBookingSetMenuMutation,
    useCancelJobMutation,
    useDeleteItineraryItemMutation,
    useEditReservationMutation,
    useGetIndividualReservationQuery,
    useUpdateBookingSetMenuMutation,
    useUpdateItineraryItemMutation,
} from '../../../../../app/Slices/SevenDayApiSlice';
import { CustomSmallListChip } from '../../../../../layout/styledcomponents/CustomChip';
import { theme } from '../../../../../shared/theme';
import { camelCaseToTitleCase } from '../../../../../shared/utils';
import { ItineraryItemType } from '../../../../enums/itinerary';
import toast from '../../../../shared/Toast';
import {
    ItineraryItemDto,
    ItineraryItemInfoDto,
    ItineraryItemLinkedBookingInfo,
    ItineraryItemPayload,
} from '../../../../types/itinerary';
import CancelReservationModal from '../CancelReservationModal';
import {
    getItineraryItemColor,
    getItineraryItemIcon,
} from './itineraryConstants';
import { Status } from '../../../../enums/booking';
import ItineraryItemForm, {
    ItineraryItemFormValues,
} from './ItineraryItemForm';
import ItineraryItemDeleteModal from './ItineraryItemDeleteModal';
import { groupJobItemsByTemplate } from '../../ReservationRequest/ReservationJobs/ReservationJobForm';
import {
    getItineraryItemStatusColor,
    getTitle,
    groupSetMenuItemsByCourse,
    hasSelectedItems,
} from './ItineraryUtils';
import { ProviderType } from '../../../../../app/Slices/SevenDayApiTypes';
import { useEditJobMutation } from '../../../../../app/Slices/SevenDayApiSlice';
import { UpdateBookingSetMenu } from '../../../../types/set-menu';
import ItineraryItemCancelJobModal from './ItineraryItemCancelJobModal';
import { BookingSetMenuStatus } from '../../../../enums/set-menu';
import { Status as JobStatus } from '../../../../enums/contractor';

type Props = {
    item: ItineraryItemDto;
    label: string;
    isLast?: boolean;
    isFirst?: boolean;
    showMap: boolean;
    setSelectedItem: (item: ItineraryItemDto) => void;
    handleOpenItineraryItem: (bookingId: number) => void;
    reservation: any;
};

export default function ItineraryItemCard({
    item,
    label,
    isLast = false,
    isFirst = false,
    showMap = false,
    setSelectedItem,
    handleOpenItineraryItem,
    reservation,
}: Props) {
    const [expanded, setExpanded] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCancelJobModal, setShowCancelJobModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [updateJob] = useEditJobMutation();
    const [updateBookingSetMenu] = useUpdateBookingSetMenuMutation();
    const [cancelJob] = useCancelJobMutation();
    const [cancelBookingSetMenu] = useCancelBookingSetMenuMutation();
    const [showCancelReservationModal, setShowCancelReservationModal] =
        useState(false);
    const [deleteItineraryItem, { isLoading: isDeleting }] =
        useDeleteItineraryItemMutation();

    const [updateItineraryItem, { isLoading: isUpdating }] =
        useUpdateItineraryItemMutation();

    const Icon = getItineraryItemIcon(item.itineraryItemInfo);
    const iconColor = getItineraryItemColor(item.itineraryItemInfo);

    const formatTime = (time: string) => {
        return format(parse(time, 'HH:mm', new Date()), 'h:mm a');
    };
    const [editReservation, { isLoading: isEditing }] =
        useEditReservationMutation();

    const { data: individualReservation, isLoading: isLoadingReservation } =
        useGetIndividualReservationQuery(
            `/bookings/${(item.itineraryItemInfo as ItineraryItemLinkedBookingInfo).bookingId}`,
            {
                refetchOnMountOrArgChange: true,
                skip: !item.is7DayBooking,
            },
        );

    const getSubtitle = (): string => {
        const info = item.itineraryItemInfo;
        switch (info.itemType) {
            case ItineraryItemType.Flight:
                return `${info.departureAirport.code} → ${info.arrivalAirport.code}`;
            case ItineraryItemType.Transfer:
                if (info.pickupAirport)
                    return `From ${info.pickupAirport.code} airport`;
                if (info.dropoffAirport)
                    return `To ${info.dropoffAirport.code} airport`;
                if (info.vehicleType) return info.vehicleType;
                return '';
            case ItineraryItemType.Event:
                return info.venue ?? '';
            case ItineraryItemType.Restaurant:
                return info.reservationName
                    ? `Booking: ${info.reservationName}`
                    : '';
            case ItineraryItemType.Property:
            case ItineraryItemType.Commercial:
            case ItineraryItemType.Vehicle:
            case ItineraryItemType.Boat:
            case ItineraryItemType.Activity:
                return info.name;
            case ItineraryItemType.PartnerService:
                return info.providerType === ProviderType.Owner
                    ? 'Set Menu'
                    : '';
            default:
                return '';
        }
    };

    const detailInfo = item.itineraryItemInfo;
    const detailRows: { label: string; value: string }[] = [];

    switch (detailInfo.itemType) {
        case ItineraryItemType.Flight:
            if (detailInfo.gate)
                detailRows.push({ label: 'Gate', value: detailInfo.gate });
            if (detailInfo.seatNumber)
                detailRows.push({
                    label: 'Seat',
                    value: detailInfo.seatNumber,
                });
            if (detailInfo.departureAirport?.terminal) {
                detailRows.push({
                    label: 'Dep. Terminal',
                    value: detailInfo.departureAirport.terminal,
                });
            }
            if (detailInfo.arrivalAirport?.terminal) {
                detailRows.push({
                    label: 'Arr. Terminal',
                    value: detailInfo.arrivalAirport.terminal,
                });
            }
            break;
        case ItineraryItemType.Event:
            if (detailInfo.dressCode)
                detailRows.push({
                    label: 'Dress Code',
                    value: detailInfo.dressCode,
                });
            break;
        case ItineraryItemType.Transfer:
            if (detailInfo.vehicleType)
                detailRows.push({
                    label: 'Vehicle',
                    value: detailInfo.vehicleType,
                });
            if (detailInfo.dropoffAddress) {
                detailRows.push({
                    label: 'Drop-off',
                    value: `${detailInfo.dropoffAddress.streetAddress}, ${detailInfo.dropoffAddress.suburb}`,
                });
            }
            break;
        default:
            break;
    }

    if (item.refNumber)
        detailRows.push({ label: 'Ref #', value: item.refNumber });
    if (item.address) {
        detailRows.push({
            label: 'Address',
            value: `${item.address.streetAddress}, ${item.address.suburb}, ${item.address.state}`,
        });
    }

    const subtitle = getSubtitle();
    const showSelectedItems = hasSelectedItems(item);
    const guestNotes =
        detailInfo.itemType === ItineraryItemType.PartnerService
            ? detailInfo.guestNotes?.trim()
            : '';
    const ownerNotes =
        detailInfo.itemType === ItineraryItemType.PartnerService
            ? detailInfo.ownerNotes?.trim()
            : '';
    const partnerServiceNotes = [
        { label: 'Guest notes', value: guestNotes },
        { label: 'Owner notes', value: ownerNotes },
    ].filter((note) => note.value);
    const contractorJobGroups =
        detailInfo.itemType === ItineraryItemType.PartnerService &&
        detailInfo.providerType === ProviderType.Contractor
            ? groupJobItemsByTemplate(detailInfo.items)
            : [];
    const setMenuCourseGroups =
        detailInfo.itemType === ItineraryItemType.PartnerService &&
        detailInfo.providerType === ProviderType.Owner
            ? groupSetMenuItemsByCourse(detailInfo.setMenu.items)
            : [];

    const sectionLabelSx = {
        fontWeight: 700,
        fontSize: 12,
        color: theme.palette.text.secondary,
    };
    const subheadingSx = {
        fontWeight: 700,
        fontSize: 12,
    };
    const itemTextSx = {
        fontSize: 12,
    };
    const itemDescriptionSx = {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    };

    const handleDelete = async () => {
        try {
            if (item.is7DayBooking) {
                const request = {
                    bookerType: individualReservation?.bookerType,
                    mainBooking: null,
                };

                const url = `/bookings/${individualReservation?.id}`;

                await editReservation([url, request]).unwrap();
            } else {
                if (!item.id) return;

                deleteItineraryItem([item.mainBookingId, item.id]).unwrap();
            }
            setShowDeleteModal(false);
            toast.success('Itinerary item removed');
        } catch (error: any) {
            toast.error(
                error?.data?.message || 'Failed to remove itinerary item',
            );
        }
    };
    const handleCancelJob = async () => {
        try {
            if (
                item.itineraryItemInfo.itemType ===
                ItineraryItemType.PartnerService
            ) {
                if (
                    item.itineraryItemInfo.providerType ===
                    ProviderType.Contractor
                ) {
                    await cancelJob(item.itineraryItemInfo.id).unwrap();
                } else {
                    await cancelBookingSetMenu(
                        item.itineraryItemInfo.id,
                    ).unwrap();
                }
            }
            setShowCancelJobModal(false);
            toast.success('Job request cancelled');
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to cancel job request');
        }
    };

    const shouldNotShowJobActions =
        item.itineraryItemInfo.itemType === ItineraryItemType.PartnerService &&
        ((item.itineraryItemInfo.providerType === ProviderType.Contractor &&
            [
                JobStatus.Cancelled,
                JobStatus.Declined,
                JobStatus.Completed,
            ].includes(item.itineraryItemInfo.status)) ||
            (item.itineraryItemInfo.providerType === ProviderType.Owner &&
                [
                    BookingSetMenuStatus.Cancelled,
                    BookingSetMenuStatus.Declined,
                    BookingSetMenuStatus.Completed,
                ].includes(item.itineraryItemInfo.status)));

    const handleEdit = (
        payload: ItineraryItemPayload | ItineraryItemFormValues,
    ) => {
        try {
            if (
                payload.itineraryItemInfo.itemType ===
                    ItineraryItemType.PartnerService &&
                payload.jobs
            ) {
                if (payload.jobs.providerType === ProviderType.Contractor) {
                    updateJob([
                        (payload.jobs as any).id,
                        { ...payload.jobs },
                    ]).unwrap();
                } else {
                    updateBookingSetMenu([
                        (payload.jobs as any).id,
                        payload.jobs as UpdateBookingSetMenu,
                    ]).unwrap();
                }
            } else {
                if (!item.id) return;
                updateItineraryItem([
                    item.mainBookingId,
                    item.id,
                    payload as ItineraryItemPayload,
                ]).unwrap();
            }
            toast.success('Itinerary item updated');
            setShowEditModal(false);
        } catch (error: any) {
            toast.error(
                error?.data?.message || 'Failed to update itinerary item',
            );
        }
    };

    const isLinkedBookingInfo = (
        info: ItineraryItemInfoDto,
    ): info is ItineraryItemLinkedBookingInfo => {
        return (
            info.itemType === ItineraryItemType.Property ||
            info.itemType === ItineraryItemType.Commercial ||
            info.itemType === ItineraryItemType.Vehicle ||
            info.itemType === ItineraryItemType.Boat ||
            info.itemType === ItineraryItemType.Activity
        );
    };
    const shouldShowStatus = (itemType: ItineraryItemType): boolean => {
        return (
            itemType === ItineraryItemType.Property ||
            itemType === ItineraryItemType.Commercial ||
            itemType === ItineraryItemType.Vehicle ||
            itemType === ItineraryItemType.Boat ||
            itemType === ItineraryItemType.Activity
        );
    };

    const disableDelete =
        (isLinkedBookingInfo(item.itineraryItemInfo) &&
            item.itineraryItemInfo.isAddOn) ||
        item.itineraryItemInfo.itemType === ItineraryItemType.PartnerService;

    const disableEdit =
        isLinkedBookingInfo(item.itineraryItemInfo) &&
        [Status.Cancelled, Status.Declined, Status.Completed].includes(
            item.itineraryItemInfo.status,
        );

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                position: 'relative',
                '&::before':
                    isFirst || isLast
                        ? {
                              content: '""',
                              position: 'absolute',
                              left: 14,
                              top: isLast ? '20px' : 0,
                              bottom: isFirst ? 'calc(100% - 20px)' : 0,
                              width: '4px',
                              bgcolor: theme.palette.background.default,
                          }
                        : {},
            }}
        >
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: theme.palette.background.default,
                    zIndex: 2,
                    borderRadius: '50%',
                    marginTop: '10px',
                }}
            >
                <Icon sx={{ fontSize: 18, color: iconColor }} />
            </Box>

            <Box
                aria-label={label}
                onClick={() => {
                    if (showMap) setSelectedItem(item);
                }}
                sx={{
                    borderRadius: 2,
                    border: `1px dashed ${lighten(theme.palette.primary.main, 0.5)}`,
                    boxShadow: `3px 3px 11px 0px ${lighten(theme.palette.custom.grey, 0.7)}`,
                    width: '100%',
                    p: 1,
                    '&:hover': {
                        backgroundColor: showMap
                            ? alpha(theme.palette.primary.main, 0.05)
                            : 'transparent',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                    }}
                    onClick={() => {
                        if (!showMap) setExpanded(!expanded);
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ width: '100%' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 13,
                                            lineHeight: 1.3,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {getTitle(item)}
                                    </Typography>

                                    {(item.itineraryItemInfo.itemType ===
                                        ItineraryItemType.Property ||
                                        item.itineraryItemInfo.itemType ===
                                            ItineraryItemType.Commercial ||
                                        item.itineraryItemInfo.itemType ===
                                            ItineraryItemType.Vehicle ||
                                        item.itineraryItemInfo.itemType ===
                                            ItineraryItemType.Boat ||
                                        item.itineraryItemInfo.itemType ===
                                            ItineraryItemType.Activity ||
                                        item.itineraryItemInfo.itemType ===
                                            ItineraryItemType.PartnerService) && (
                                        <Box
                                            sx={{
                                                ml: 0.7,
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <CustomSmallListChip
                                                sx={{
                                                    cursor: 'pointer',
                                                    size: 'small',
                                                }}
                                                label={camelCaseToTitleCase(
                                                    item.itineraryItemInfo
                                                        .status,
                                                )}
                                                color={getItineraryItemStatusColor(
                                                    item.itineraryItemInfo
                                                        .status,
                                                )}
                                                variant='outlined'
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {item.is7DayBooking && (
                                    <Box>
                                        <img
                                            src={theme.palette.custom.lightLogo}
                                            alt=''
                                            width={55}
                                            height={25}
                                            style={{ objectFit: 'contain' }}
                                        />
                                    </Box>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mt: 0.25,
                                }}
                            >
                                <Typography
                                    variant='body3'
                                    sx={{
                                        fontSize: 11,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {formatTime(item.startTime)} –{' '}
                                    {formatTime(item.endTime)}
                                </Typography>
                                {subtitle && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: theme.palette.text
                                                    .secondary,
                                            }}
                                        >
                                            •
                                        </Typography>
                                        <Typography
                                            variant='body3'
                                            sx={{
                                                fontSize: 11,
                                                color: theme.palette.text
                                                    .secondary,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {subtitle}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25,
                            }}
                            onClick={() => {
                                if (showMap) setExpanded(!expanded);
                            }}
                        >
                            <IconButton size='small'>
                                {expanded ? (
                                    <ExpandLess sx={{ fontSize: 18 }} />
                                ) : (
                                    <ExpandMore sx={{ fontSize: 18 }} />
                                )}
                            </IconButton>
                        </Box>
                    </Box>

                    {item.conflictingItineraryItems.length > 0 &&
                        !shouldNotShowJobActions && (
                            <Box
                                sx={{
                                    mt: 0.5,
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: lighten(
                                        theme.palette.error.light,
                                        0.9,
                                    ),
                                }}
                            >
                                <Typography
                                    variant='body3'
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 12,
                                        color: theme.palette.error.main,
                                    }}
                                >
                                    Overlaps with:
                                </Typography>
                                {item.conflictingItineraryItems.map(
                                    (c, idx) => (
                                        <Typography
                                            key={idx}
                                            variant='body3'
                                            sx={{
                                                fontSize: 11,
                                                color: theme.palette.error.main,
                                            }}
                                        >
                                            • {c.autoGeneratedDescription}
                                        </Typography>
                                    ),
                                )}
                            </Box>
                        )}
                </Box>

                <Collapse in={expanded}>
                    <Box
                        sx={{
                            borderTop: `1px solid ${lighten(theme.palette.custom.grey, 0.5)}`,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            sx={{
                                mt: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            {detailRows.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'auto 1fr',
                                        gap: 0.5,
                                        columnGap: 2,
                                    }}
                                >
                                    {detailRows.map((row) => (
                                        <React.Fragment key={row.label}>
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    fontWeight: 700,
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontSize: 12,
                                                }}
                                            >
                                                {row.label}
                                            </Typography>
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    fontSize: 12,
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {row.value}
                                            </Typography>
                                        </React.Fragment>
                                    ))}
                                </Box>
                            )}

                            {showSelectedItems && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                    }}
                                >
                                    {detailInfo.itemType ===
                                        ItineraryItemType.PartnerService &&
                                        detailInfo.providerType ===
                                            ProviderType.Owner && (
                                            <Typography
                                                variant='body3'
                                                sx={itemTextSx}
                                            >
                                                {detailInfo.name} ×{' '}
                                                {detailInfo.quantity} guests
                                            </Typography>
                                        )}
                                    {detailInfo.itemType ===
                                        ItineraryItemType.PartnerService &&
                                        detailInfo.providerType ===
                                            ProviderType.Contractor &&
                                        contractorJobGroups.map((group) => (
                                            <Box key={group.template.id}>
                                                <Typography
                                                    variant='body3'
                                                    sx={subheadingSx}
                                                >
                                                    {group.template.name}
                                                </Typography>
                                                {group.jobItems.map(
                                                    (jobItem, index) => (
                                                        <Box
                                                            key={
                                                                jobItem.id ??
                                                                index
                                                            }
                                                        >
                                                            <Typography
                                                                sx={itemTextSx}
                                                            >
                                                                - {jobItem.name}{' '}
                                                                ×{' '}
                                                                {
                                                                    jobItem.quantity
                                                                }
                                                            </Typography>
                                                            {jobItem.description && (
                                                                <Typography
                                                                    sx={
                                                                        itemDescriptionSx
                                                                    }
                                                                >
                                                                    {
                                                                        jobItem.description
                                                                    }
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ),
                                                )}
                                            </Box>
                                        ))}
                                    {detailInfo.itemType ===
                                        ItineraryItemType.PartnerService &&
                                        detailInfo.providerType ===
                                            ProviderType.Owner &&
                                        setMenuCourseGroups.map((group) => (
                                            <Box key={group.value}>
                                                <Typography
                                                    variant='body3'
                                                    sx={subheadingSx}
                                                >
                                                    {group.label}
                                                </Typography>
                                                {group.items.map(
                                                    (menuItem, index) => (
                                                        <Box
                                                            key={
                                                                menuItem.id ??
                                                                index
                                                            }
                                                        >
                                                            <Typography
                                                                sx={itemTextSx}
                                                            >
                                                                -{' '}
                                                                {menuItem.name}
                                                            </Typography>
                                                            {menuItem.description && (
                                                                <Typography
                                                                    sx={
                                                                        itemDescriptionSx
                                                                    }
                                                                >
                                                                    {
                                                                        menuItem.description
                                                                    }
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ),
                                                )}
                                            </Box>
                                        ))}
                                </Box>
                            )}

                            {partnerServiceNotes.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                    }}
                                >
                                    {partnerServiceNotes.map((note) => (
                                        <Box key={note.label}>
                                            <Typography
                                                variant='body3'
                                                sx={sectionLabelSx}
                                            >
                                                {note.label}{' '}
                                            </Typography>
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    fontSize: 12,
                                                    color: theme.palette.text
                                                        .secondary,
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {note.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {item.drivingTimeFromMainBooking != null && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <DriveEta
                                        sx={{
                                            fontSize: 14,
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        {item.drivingTimeFromMainBooking} min
                                        {item.drivingDistanceKmFromMainBooking !=
                                            null &&
                                            ` · ${item.drivingDistanceKmFromMainBooking.toFixed(1)} km`}{' '}
                                        from property
                                    </Typography>
                                </Box>
                            )}

                            {item.notes && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 0.5,
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <Notes
                                        sx={{
                                            fontSize: 14,
                                            color: theme.palette.text.secondary,
                                            mt: 0.25,
                                        }}
                                    />
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.text.secondary,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {item.notes}
                                    </Typography>
                                </Box>
                            )}

                            {item.attachments.length > 0 && (
                                <Box>
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 12,
                                            mb: 0.5,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        Attachments
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1.5,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {item.attachments.map((att) => (
                                            <Box
                                                key={att.uuid}
                                                component='a'
                                                href={att.src}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.1,
                                                    color: theme.palette.text
                                                        .primary,
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        color: theme.palette
                                                            .primary.main,
                                                    },
                                                }}
                                            >
                                                <AttachFile
                                                    sx={{ fontSize: 13 }}
                                                />
                                                <Typography
                                                    component='span'
                                                    variant='body3'
                                                    sx={{
                                                        fontSize: 11,
                                                        textDecoration:
                                                            'underline',
                                                        overflow: 'hidden',
                                                        '&:hover': {
                                                            color: theme.palette
                                                                .primary.main,
                                                        },
                                                    }}
                                                >
                                                    {att.name.length > 15
                                                        ? `${att.name.substring(0, 15)}...`
                                                        : att.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {detailRows.length === 0 &&
                                !showSelectedItems &&
                                partnerServiceNotes.length === 0 &&
                                !item.drivingTimeFromMainBooking &&
                                !item.notes &&
                                !item.attachments.length && (
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        No details have been added yet
                                    </Typography>
                                )}
                        </Box>
                        {
                            <Box sx={{ alignSelf: 'flex-end' }}>
                                {item.is7DayBooking && (
                                    <CustomTooltip title='Open reservation'>
                                        <IconButton
                                            size='small'
                                            onClick={() =>
                                                handleOpenItineraryItem(
                                                    (
                                                        item.itineraryItemInfo as ItineraryItemLinkedBookingInfo
                                                    ).bookingId,
                                                )
                                            }
                                            sx={{
                                                color: theme.palette.text
                                                    .secondary,
                                            }}
                                        >
                                            <OpenInNew sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </CustomTooltip>
                                )}
                                {!shouldNotShowJobActions && (
                                    <CustomTooltip title='Edit'>
                                        <IconButton
                                            size='small'
                                            onClick={() =>
                                                setShowEditModal(true)
                                            }
                                            disabled={disableEdit}
                                            sx={{
                                                color: theme.palette.text
                                                    .secondary,
                                            }}
                                        >
                                            <Edit sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </CustomTooltip>
                                )}
                                {!disableDelete && (
                                    <CustomTooltip title='Remove from itinerary'>
                                        <IconButton
                                            size='small'
                                            onClick={() =>
                                                setShowDeleteModal(true)
                                            }
                                            sx={{
                                                color: theme.palette.text
                                                    .secondary,
                                            }}
                                        >
                                            <Delete sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </CustomTooltip>
                                )}
                                {item.is7DayBooking && (
                                    <CustomTooltip title='Cancel reservation'>
                                        <IconButton
                                            size='small'
                                            onClick={() =>
                                                setShowCancelReservationModal(
                                                    true,
                                                )
                                            }
                                            disabled={disableEdit}
                                            sx={{
                                                color: theme.palette.text
                                                    .secondary,
                                            }}
                                        >
                                            <EventBusy sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </CustomTooltip>
                                )}
                                {item.itineraryItemInfo.itemType ===
                                    ItineraryItemType.PartnerService &&
                                    !shouldNotShowJobActions && (
                                        <CustomTooltip title='Cancel job request'>
                                            <IconButton
                                                size='small'
                                                onClick={() =>
                                                    setShowCancelJobModal(true)
                                                }
                                                disabled={disableEdit}
                                                sx={{
                                                    color: theme.palette.text
                                                        .secondary,
                                                }}
                                            >
                                                <EventBusy
                                                    sx={{ fontSize: 16 }}
                                                />
                                            </IconButton>
                                        </CustomTooltip>
                                    )}
                            </Box>
                        }

                        <ItineraryItemForm
                            open={showEditModal}
                            onClose={() => setShowEditModal(false)}
                            onSubmit={handleEdit}
                            existingItem={item}
                            mainBooking={reservation}
                            isAddOnForm={false}
                        />
                        <ItineraryItemDeleteModal
                            open={showDeleteModal}
                            isDeleting={isDeleting}
                            onClose={() => setShowDeleteModal(false)}
                            onKeepItem={() => setShowDeleteModal(false)}
                            onRemoveItem={handleDelete}
                        />
                        <ItineraryItemCancelJobModal
                            open={showCancelJobModal}
                            isDeleting={isDeleting}
                            onClose={() => setShowCancelJobModal(false)}
                            onKeepItem={() => setShowCancelJobModal(false)}
                            onRemoveItem={handleCancelJob}
                        />

                        {isLinkedBookingInfo(item.itineraryItemInfo) && (
                            <CancelReservationModal
                                open={showCancelReservationModal}
                                onClose={() =>
                                    setShowCancelReservationModal(false)
                                }
                                onChange={() =>
                                    setShowCancelReservationModal(false)
                                }
                                component='reservation'
                                id={item.itineraryItemInfo.bookingId}
                            />
                        )}
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
}
